import * as yup from "yup";
import { useTranslation } from "@shared/utils/internationalization";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { useAndroidTWACheck } from "@shared/hooks/platform/useAndroidTWACheck";
import { isPlatform } from "@shared/utils/get-platform";
import React, { useCallback, useState } from "react";
import { passwordRegex } from "@shared/utils/password/utils";
import { Controller, useForm } from "react-hook-form";
import axios from "axios";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import FormControl from "@ui-kit/form/FormControl/FormControl.web";
import FormLabel from "@ui-kit/form/FormLabel/FormLabel.web";

import Divider from "@ui-kit/data-display/Divider/Divider.web";
import Box from "@ui-kit/layout/Box/Box.web";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import Button from "@ui-kit/form/Button/Button.web";
import Link from "@ui-kit/navigation/Link/Link.web";
import { yupResolver } from "@hookform/resolvers/yup";
import Input, {
  InputGroup,
  InputRightElement,
} from "@ui-kit/form/Input/Input.web";
import FormErrorMessage from "@ui-kit/form/FormErrorMessage/FormErrorMessage.web";
import useRecaptcha from "@shared/modules/auth/hooks/useRecaptcha";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { logAppInfo } from "@shared/utils/errors/errorLogger";
import CaptchaBadgeStyler from "@ui-application/other/CaptchaBadgeStyler/CaptchaBadgeStyler";
import FacebookLoginButton from "../../FacebookLoginButton/FacebookLoginButton.web";
import GoogleLoginButton from "../../GoogleLoginButton/GoogleLoginButton.web";
import type { CreateAccountFormSharedProps } from "./CreateAccountForm.props";

const notifier = NotifierFactory.createNotifier();

export type CreateAccountFormProps = CreateAccountFormSharedProps & {
  // Add any web specific props here.
};

type CreateAccountFormData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
};

export default function CreateAccountForm(
  props: CreateAccountFormProps
): JSX.Element {
  const { onSuccess } = props;
  const { createAnAccount } = useUserSessionAuthState();
  const { t } = useTranslation("common");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { executeRecaptcha } = useRecaptcha();

  // Facebook login is unsupported on Android WebViews.
  const isAndroidCompatible = useAndroidTWACheck();

  const createAccountSchema = yup.object().shape({
    firstName: yup.string().required(t("First name is required")),
    lastName: yup.string().required(t("Last name is required")),
    email: yup
      .string()
      .required(t("Email is required"))
      .email(t("Invalid email address")),
    password: yup
      .string()
      .required(t("Password is required"))
      .min(7, t("Password needs to be at least 7 characters"))
      .matches(passwordRegex, "Password needs at least one special character"),
    confirmPassword: yup
      .string()
      .required(t("Passwords must match"))
      .oneOf([yup.ref("password"), null], t("Passwords must match")),
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CreateAccountFormData>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - types are correct for yupResolver
    resolver: yupResolver(createAccountSchema),
  });

  const onSubmit = useCallback(
    async (data: CreateAccountFormData) => {
      const { firstName, lastName, email, password, confirmPassword } = data;
      const recaptchaToken = await executeRecaptcha("account_creation");
      try {
        await createAnAccount({
          first_name: firstName,
          last_name: lastName,
          email,
          password1: password,
          password2: confirmPassword,
          remember: true,
          captcha_token: recaptchaToken,
        });
        notifier.success(t("Account created!"));
        onSuccess();
      } catch (error) {
        if (axios.isAxiosError(error)) {
          logAppInfo(error.response?.data?.detail);
        }
      }
    },
    [createAnAccount, onSuccess, t]
  );
  return (
    <>
      <CaptchaBadgeStyler />
      <form onSubmit={handleSubmit(onSubmit)} data-sentry-mask>
        <Flex direction={{ base: "column", sm: "row" }}>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <FormControl isInvalid={Boolean(errors.firstName)}>
                <FormLabel htmlFor="firstName">{t("First name")}</FormLabel>
                <Input
                  {...field}
                  id="firstName"
                  placeholder={t("First name")}
                  data-testid="first-name"
                />
                <FormErrorMessage>{errors.firstName?.message}</FormErrorMessage>
              </FormControl>
            )}
          />
          <Box w="element.xl" h="element.lg" />
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <FormControl isInvalid={Boolean(errors.lastName)}>
                <FormLabel htmlFor="lastName">{t("Last name")}</FormLabel>
                <Input
                  {...field}
                  id="lastName"
                  placeholder={t("Last name")}
                  data-testid="last-name-input"
                />
                <FormErrorMessage>{errors.lastName?.message}</FormErrorMessage>
              </FormControl>
            )}
          />
        </Flex>
        <Box h="element.lg" />
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <FormControl isInvalid={Boolean(errors.email)}>
              <FormLabel htmlFor="email">{t("Email address")}</FormLabel>
              <Input
                {...field}
                id="email"
                placeholder={t("Email address")}
                data-testid="email-address-input"
              />
              <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
            </FormControl>
          )}
        />
        <Box h="element.lg" />
        <Flex direction={{ base: "column", sm: "row" }}>
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <FormControl isInvalid={Boolean(errors.password)}>
                <FormLabel htmlFor="password">{t("Password")}</FormLabel>
                <InputGroup>
                  <Input
                    {...field}
                    id="password"
                    placeholder={t("Password")}
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    data-testid="password-input"
                  />
                  <InputRightElement pr="3xs">
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label="show password"
                      onClick={() => setShowPassword(!showPassword)}
                      iconName={showPassword ? "Hide" : "Show"}
                      iconColor="font.dark"
                      iconOutline={false}
                      _active={{ backgroundColor: "transparent" }}
                      _hover={{ backgroundColor: "transparent" }}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
              </FormControl>
            )}
          />
          <Box w="element.xl" h="element.lg" />
          <Controller
            name="confirmPassword"
            control={control}
            render={({ field }) => (
              <FormControl isInvalid={Boolean(errors.confirmPassword)}>
                <FormLabel htmlFor="confirmPassword">
                  {t("Confirm password")}
                </FormLabel>
                <InputGroup>
                  <Input
                    {...field}
                    id="confirmPassword"
                    placeholder={t("Confirm password")}
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    data-testid="confirm-password-input"
                  />
                  <InputRightElement pr="3xs">
                    <IconButton
                      size="sm"
                      variant="ghost"
                      aria-label="show confirm password"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      iconName={showConfirmPassword ? "Hide" : "Show"}
                      iconColor="font.dark"
                      iconOutline={false}
                      _active={{ backgroundColor: "transparent" }}
                      _hover={{ backgroundColor: "transparent" }}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>
                  {errors.confirmPassword?.message}
                </FormErrorMessage>
              </FormControl>
            )}
          />
        </Flex>
        <Box h="element.4xl" />
        <Button
          width="100%"
          variant="solid"
          isLoading={isSubmitting}
          type="submit"
          data-testid="create-account-button"
        >
          {t("Create account")}
        </Button>
      </form>

      {!isPlatform("desktop") &&
        (isAndroidCompatible == null || isAndroidCompatible === true) && (
          <>
            <Box h="element.2xl" />
            <Flex alignItems="center">
              <Divider width="100%" />
              <Box width="100%" textAlign="center" color="gray.600">
                {t("Or log in with")}
              </Box>
              <Divider width="100%" />
            </Flex>
            <Box h="element.2xl" />
            <Flex gap="2xs">
              <GoogleLoginButton title={t("Google")} onSuccess={onSuccess} />
              <FacebookLoginButton
                title={t("Facebook")}
                onSuccess={onSuccess}
              />
            </Flex>
          </>
        )}

      <Box h="element.2xl" />
      <Box width="100%" color="gray.500" fontSize="xs">
        {t("By creating an account you agree to our")}{" "}
        <Link
          fontSize="xs"
          color="secondary.500"
          href="/sell/privacy-policy"
          external
          target="_blank"
          boxShadow="none"
        >
          {t("privacy policy")}
        </Link>{" "}
        &{" "}
        <Link
          fontSize="xs"
          color="secondary.500"
          href="/sell/terms-of-service"
          external
          target="_blank"
          boxShadow="none"
        >
          {t("terms of service")}
        </Link>
        .
      </Box>
    </>
  );
}
